/**
 * Die Fenster rund um einen Auszug: Kopfdaten korrigieren, eine Buchung
 * anlegen oder aendern, trotz Befund freigeben, loeschen, die Art festlegen.
 *
 * Jede Aenderung wird als von Hand markiert und nimmt eine alte Freigabe
 * zurueck. Danach rechnet der Server alles neu.
 */
import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { api, ApiFehler, type AuszugVoll, type Befund, type Buchung } from '../lib/api'
import { datum, euro } from '../lib/format'
import { pfad, type Art } from '../lib/art'
import { Fenster } from './Fenster'

function fehlerText(problem: unknown, rueckfall: string): string {
  return problem instanceof ApiFehler ? problem.message : rueckfall
}

/** Betrag fuer ein Eingabefeld: "1.234,56" ohne Vorzeichen. */
function betragFeld(cent: string | null): string {
  if (cent === null) return ''
  return euro(cent).replace('−', '-')
}

/* ─────────────────────────────────────────────────── Kopfdaten */

export function KopfBearbeiten({ auszug, offen, aufSchliessen }: { auszug: AuszugVoll; offen: boolean; aufSchliessen: () => void }) {
  const abfragen = useQueryClient()
  const [werte, setzeWerte] = useState(() => ({
    iban: auszug.iban ?? '',
    bic: auszug.bic ?? '',
    bank: auszug.bank ?? '',
    inhaber: auszug.inhaber ?? '',
    auszug_nummer: auszug.auszug_nummer ?? '',
    zeitraum_von: auszug.zeitraum_von ? datum(auszug.zeitraum_von) : '',
    zeitraum_bis: auszug.zeitraum_bis ? datum(auszug.zeitraum_bis) : '',
    anfangssaldo: betragFeld(auszug.anfangssaldo_cent),
    schlusssaldo: betragFeld(auszug.schlusssaldo_cent),
  }))
  const [fehler, setzeFehler] = useState<{ text: string; feld?: string } | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  const feld = (name: keyof typeof werte) => ({
    value: werte[name],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setzeWerte({ ...werte, [name]: e.target.value }),
    'aria-invalid': fehler?.feld === name,
    autoComplete: 'off',
    'data-1p-ignore': true,
  })

  async function absenden(e: FormEvent) {
    e.preventDefault()
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await api.aendern(`/api/auszuege/${auszug.id}`, Object.fromEntries(Object.entries(werte).map(([k, v]) => [k, v.trim() || null])))
      await abfragen.invalidateQueries()
      aufSchliessen()
    } catch (problem) {
      setzeFehler({ text: fehlerText(problem, 'Die Änderung konnte nicht gespeichert werden.'), ...(problem instanceof ApiFehler && problem.feld ? { feld: problem.feld } : {}) })
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel="Kopfdaten des Auszugs" offen={offen} aufSchliessen={aufSchliessen} breit>
      <form className="formular" onSubmit={(e) => void absenden(e)}>
        {fehler && <div className="fehler">{fehler.text}</div>}
        <div className="feldreihe">
          <div><label htmlFor="k-iban">IBAN</label><input id="k-iban" type="text" className="zahl" {...feld('iban')} /></div>
          <div className="schmal"><label htmlFor="k-bic">BIC</label><input id="k-bic" type="text" className="zahl" {...feld('bic')} /></div>
        </div>
        <div className="feldreihe">
          <div><label htmlFor="k-bank">Bank</label><input id="k-bank" type="text" {...feld('bank')} /></div>
          <div><label htmlFor="k-inh">Kontoinhaber</label><input id="k-inh" type="text" {...feld('inhaber')} /></div>
        </div>
        <div className="feldreihe">
          <div><label htmlFor="k-nr">Auszugsnummer</label><input id="k-nr" type="text" {...feld('auszug_nummer')} /></div>
          <div><label htmlFor="k-von">Zeitraum von</label><input id="k-von" type="text" className="zahl" placeholder="TT.MM.JJJJ" {...feld('zeitraum_von')} /></div>
          <div><label htmlFor="k-bis">bis</label><input id="k-bis" type="text" className="zahl" placeholder="TT.MM.JJJJ" {...feld('zeitraum_bis')} /></div>
        </div>
        <div className="feldreihe">
          <div><label htmlFor="k-anf">Anfangssaldo</label><input id="k-anf" type="text" className="zahl" placeholder="1.234,56 oder -1.234,56" {...feld('anfangssaldo')} /></div>
          <div><label htmlFor="k-schl">Schlusssaldo</label><input id="k-schl" type="text" className="zahl" placeholder="1.234,56 oder -1.234,56" {...feld('schlusssaldo')} /></div>
        </div>
        <div className="hinweis">Salden mit Minus, wenn das Konto im Soll steht. Ein leeres Feld heißt: steht nicht auf dem Auszug.</div>
        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={aufSchliessen}>Abbrechen</button>
          <button type="submit" className="knopf gefuellt" disabled={laeuft}>{laeuft ? 'Wird gespeichert …' : 'Speichern'}</button>
        </div>
      </form>
    </Fenster>
  )
}

/* ─────────────────────────────────────────────────── Buchung */

export function BuchungBearbeiten({
  auszugId,
  buchung,
  offen,
  aufSchliessen,
}: {
  auszugId: string
  buchung: Buchung | null
  offen: boolean
  aufSchliessen: () => void
}) {
  const abfragen = useQueryClient()
  const neu = buchung === null
  const [werte, setzeWerte] = useState(() => ({
    buchungsdatum: buchung?.buchungsdatum ? datum(buchung.buchungsdatum) : '',
    valuta: buchung?.valuta ? datum(buchung.valuta) : '',
    betrag: buchung ? betragFeld(buchung.betrag_cent) : '',
    buchungstext: buchung?.buchungstext ?? '',
    gegenpartei: buchung?.gegenpartei ?? '',
    gegen_iban: buchung?.gegen_iban ?? '',
    gegen_bic: buchung?.gegen_bic ?? '',
    verwendungszweck: buchung?.verwendungszweck ?? '',
    end_to_end_id: buchung?.end_to_end_id ?? '',
    mandatsreferenz: buchung?.mandatsreferenz ?? '',
    glaeubiger_id: buchung?.glaeubiger_id ?? '',
  }))
  const [richtung, setzeRichtung] = useState<'CRDT' | 'DBIT'>(buchung?.richtung ?? 'DBIT')
  const [fehler, setzeFehler] = useState<{ text: string; feld?: string } | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  const [loeschenFragen, setzeLoeschenFragen] = useState(false)

  const feld = (name: keyof typeof werte) => ({
    value: werte[name],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setzeWerte({ ...werte, [name]: e.target.value }),
    'aria-invalid': fehler?.feld === name,
    autoComplete: 'off',
    'data-1p-ignore': true,
  })

  async function absenden(e: FormEvent) {
    e.preventDefault()
    setzeFehler(null)
    setzeLaeuft(true)
    const koerper = {
      ...Object.fromEntries(Object.entries(werte).map(([k, v]) => [k, v.trim() || null])),
      buchungsdatum: werte.buchungsdatum.trim(),
      betrag: werte.betrag.trim(),
      richtung,
      seite: buchung?.seite ?? null,
    }
    try {
      if (neu) await api.senden(`/api/auszuege/${auszugId}/buchungen`, koerper)
      else await api.aendern(`/api/auszuege/${auszugId}/buchungen/${buchung.id}`, koerper)
      await abfragen.invalidateQueries()
      aufSchliessen()
    } catch (problem) {
      setzeFehler({ text: fehlerText(problem, 'Die Buchung konnte nicht gespeichert werden.'), ...(problem instanceof ApiFehler && problem.feld ? { feld: problem.feld } : {}) })
    } finally {
      setzeLaeuft(false)
    }
  }

  async function loeschen() {
    if (!buchung) return
    setzeLaeuft(true)
    try {
      await api.loeschen(`/api/auszuege/${auszugId}/buchungen/${buchung.id}`)
      await abfragen.invalidateQueries()
      aufSchliessen()
    } catch (problem) {
      setzeFehler({ text: fehlerText(problem, 'Die Buchung konnte nicht gelöscht werden.') })
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel={neu ? 'Buchung hinzufügen' : `Buchung ${buchung.position}`} offen={offen} aufSchliessen={aufSchliessen} breit>
      <form className="formular" onSubmit={(e) => void absenden(e)}>
        {fehler && <div className="fehler">{fehler.text}</div>}
        {buchung?.betrag_gedruckt && (
          <div className="leiser">So gelesen: <span className="zahl">{buchung.betrag_gedruckt}</span>{buchung.seite ? `, Seite ${buchung.seite}` : ''}</div>
        )}
        <div className="feldreihe">
          <div><label htmlFor="b-dat">Buchungstag</label><input id="b-dat" type="text" className="zahl" placeholder="TT.MM.JJJJ" required {...feld('buchungsdatum')} /></div>
          <div><label htmlFor="b-val">Wertstellung</label><input id="b-val" type="text" className="zahl" placeholder="TT.MM.JJJJ" {...feld('valuta')} /></div>
          <div><label htmlFor="b-betrag">Betrag</label><input id="b-betrag" type="text" className="zahl" placeholder="1.234,56" required {...feld('betrag')} /></div>
        </div>
        <div>
          <label>Richtung</label>
          <div className="wahl">
            <button type="button" aria-pressed={richtung === 'DBIT'} onClick={() => setzeRichtung('DBIT')}>Soll, belastet das Konto</button>
            <button type="button" aria-pressed={richtung === 'CRDT'} onClick={() => setzeRichtung('CRDT')}>Haben, Eingang</button>
          </div>
        </div>
        <div className="feldreihe">
          <div><label htmlFor="b-text">Buchungstext</label><input id="b-text" type="text" placeholder="SEPA-Lastschrift" {...feld('buchungstext')} /></div>
          <div><label htmlFor="b-partner">Zahlungspartner</label><input id="b-partner" type="text" {...feld('gegenpartei')} /></div>
        </div>
        <div className="feldreihe">
          <div><label htmlFor="b-iban">IBAN des Partners</label><input id="b-iban" type="text" className="zahl" {...feld('gegen_iban')} /></div>
          <div className="schmal"><label htmlFor="b-bic">BIC</label><input id="b-bic" type="text" className="zahl" {...feld('gegen_bic')} /></div>
        </div>
        <div>
          <label htmlFor="b-zweck">Verwendungszweck</label>
          <textarea id="b-zweck" rows={3} {...feld('verwendungszweck')} />
        </div>
        <div className="feldreihe">
          <div><label htmlFor="b-eref">End-to-End-Referenz</label><input id="b-eref" type="text" className="zahl" {...feld('end_to_end_id')} /></div>
          <div><label htmlFor="b-mref">Mandatsreferenz</label><input id="b-mref" type="text" className="zahl" {...feld('mandatsreferenz')} /></div>
          <div><label htmlFor="b-cred">Gläubiger-ID</label><input id="b-cred" type="text" className="zahl" {...feld('glaeubiger_id')} /></div>
        </div>
        <div className="formular-fuss">
          {!neu && !loeschenFragen && <button type="button" className="knopf still links" onClick={() => setzeLoeschenFragen(true)}>Buchung löschen</button>}
          {!neu && loeschenFragen && (
            <span className="links rueckfrage">
              Wirklich löschen?
              <button type="button" className="knopf klein" onClick={() => void loeschen()} disabled={laeuft}>Löschen</button>
              <button type="button" className="knopf still klein" onClick={() => setzeLoeschenFragen(false)}>Nein</button>
            </span>
          )}
          <button type="button" className="knopf still" onClick={aufSchliessen}>Abbrechen</button>
          <button type="submit" className="knopf gefuellt" disabled={laeuft}>{laeuft ? 'Wird gespeichert …' : neu ? 'Hinzufügen' : 'Speichern'}</button>
        </div>
      </form>
    </Fenster>
  )
}

/* ─────────────────────────────────────────────────── Freigabe */

export function Freigeben({
  auszugId,
  befunde,
  errechnet,
  gedruckt,
  offen,
  aufSchliessen,
}: {
  auszugId: string
  befunde: Befund[]
  /** Der Schlusssaldo aus Anfangssaldo und Buchungen, der in die Datei geht. */
  errechnet: string | null
  gedruckt: string | null
  offen: boolean
  aufSchliessen: () => void
}) {
  const abfragen = useQueryClient()
  const [grund, setzeGrund] = useState('')
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)

  async function absenden(e: FormEvent) {
    e.preventDefault()
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await api.senden(`/api/auszuege/${auszugId}/freigeben`, { grund })
      await abfragen.invalidateQueries()
      aufSchliessen()
    } catch (problem) {
      setzeFehler(fehlerText(problem, 'Die Freigabe hat nicht geklappt.'))
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel="Trotzdem freigeben" offen={offen} aufSchliessen={aufSchliessen}>
      <form className="formular" onSubmit={(e) => void absenden(e)}>
        {fehler && <div className="fehler">{fehler}</div>}
        <p className="leise">
          Offen ist: {befunde.filter((b) => b.blockt).map((b) => b.titel).join(', ')}. Freigeben heißt, der Auszug wird so exportiert, wie er jetzt ist.
          DATEV nimmt einen Import nicht zurück.
        </p>
        {errechnet !== null && gedruckt !== null && errechnet !== gedruckt && (
          <div className="hinweis-kasten">
            In die Datei geht der errechnete Schlusssaldo <span className="zahl">{euro(errechnet)}</span>, nicht der gedruckte{' '}
            <span className="zahl">{euro(gedruckt)}</span>. Eine camt-Datei muss in sich aufgehen. Weicht der Kontostand in DATEV danach von der Bank ab, zeigt die Kontenabstimmung die Differenz.
          </div>
        )}
        {gedruckt === null && errechnet !== null && (
          <div className="hinweis-kasten">Der fehlende Saldo wird aus dem vorhandenen und den Buchungen errechnet.</div>
        )}
        <div>
          <label htmlFor="f-grund">Begründung</label>
          <textarea id="f-grund" rows={3} required placeholder="Zum Beispiel: Differenz steht so auf dem Originalauszug, mit der Bank geklärt." value={grund} onChange={(e) => setzeGrund(e.target.value)} />
          <div className="hinweis">Die Begründung wird mit Name und Zeitpunkt festgehalten.</div>
        </div>
        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={aufSchliessen}>Abbrechen</button>
          <button type="submit" className="knopf gefuellt" disabled={laeuft || grund.trim().length < 5}>{laeuft ? 'Wird freigegeben …' : 'Freigeben'}</button>
        </div>
      </form>
    </Fenster>
  )
}

/* ─────────────────────────────────────────────────── Löschen */

export function AuszugLoeschen({ auszugId, zurueck, offen, aufSchliessen }: { auszugId: string; zurueck: string; offen: boolean; aufSchliessen: () => void }) {
  const abfragen = useQueryClient()
  const navigiere = useNavigate()
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)

  async function loeschen() {
    setzeLaeuft(true)
    try {
      await api.loeschen(`/api/auszuege/${auszugId}`)
      await abfragen.invalidateQueries()
      navigiere(zurueck)
    } catch (problem) {
      setzeFehler(fehlerText(problem, 'Der Auszug konnte nicht gelöscht werden.'))
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel="Auszug löschen" offen={offen} aufSchliessen={aufSchliessen}>
      <div className="formular">
        {fehler && <div className="fehler">{fehler}</div>}
        <p className="leise">Der gelesene Auszug mit allen Buchungen wird gelöscht. Gehört danach kein Auszug mehr zum Original, wird es mit entfernt.</p>
        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={aufSchliessen}>Abbrechen</button>
          <button type="button" className="knopf gefuellt loeschknopf" onClick={() => void loeschen()} disabled={laeuft}>{laeuft ? 'Wird gelöscht …' : 'Löschen'}</button>
        </div>
      </div>
    </Fenster>
  )
}

/* ─────────────────────────────────────────────────── Art festlegen */

const ART_TEXT: Record<Art, string> = { kontoauszug: 'Kontoauszug', kreditkarte: 'Kreditkarte' }

/**
 * Kontoauszug oder Kreditkarte, von einem Menschen entschieden. Bleibt die
 * Art, faellt nur die Sperre weg. Wechselt sie, wird dieser Teil der Datei in
 * der richtigen Spur neu gelesen, und der Auszug erscheint danach auf der
 * anderen Seite.
 */
export function ArtFestlegen({ auszug, offen, aufSchliessen }: { auszug: AuszugVoll; offen: boolean; aufSchliessen: () => void }) {
  const abfragen = useQueryClient()
  const navigiere = useNavigate()
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  const andere: Art = auszug.art === 'kreditkarte' ? 'kontoauszug' : 'kreditkarte'

  async function festlegen(art: Art) {
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      const antwort = await api.senden<{ ok: boolean; neuGelesen: boolean }>(`/api/auszuege/${auszug.id}/art`, { art })
      await abfragen.invalidateQueries()
      if (antwort.neuGelesen) navigiere(pfad(art, '/eingang'))
      else aufSchliessen()
    } catch (problem) {
      setzeFehler(fehlerText(problem, 'Die Art konnte nicht festgelegt werden.'))
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel="Kontoauszug oder Kreditkarte" offen={offen} aufSchliessen={aufSchliessen}>
      <div className="formular">
        {fehler && <div className="fehler">{fehler}</div>}
        <p className="leise">
          Erkannt als <strong>{ART_TEXT[auszug.art]}</strong>{auszug.art_beleg ? <> (an „{auszug.art_beleg}“)</> : null}.
        </p>
        <p className="leiser">
          Als {ART_TEXT[andere]} einordnen heißt: dieser Teil der Datei wird in der Spur für {andere === 'kreditkarte' ? 'Kreditkarten' : 'Kontoauszüge'} neu gelesen.
          Was bisher daraus gelesen wurde, wird verworfen. Die übrigen Auszüge der Datei bleiben, wie sie sind.
        </p>
        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={aufSchliessen} disabled={laeuft}>Abbrechen</button>
          <button type="button" className="knopf" onClick={() => void festlegen(andere)} disabled={laeuft}>Ist ein{andere === 'kreditkarte' ? 'e Kreditkarte' : ' Kontoauszug'}</button>
          {auszug.art_unsicher && (
            <button type="button" className="knopf gefuellt" onClick={() => void festlegen(auszug.art)} disabled={laeuft}>{laeuft ? 'Einen Moment …' : `${ART_TEXT[auszug.art]} bestätigen`}</button>
          )}
        </div>
      </div>
    </Fenster>
  )
}
